import React from 'react';
// Components
import Attachments from 'shared/Attachments';
import Modal from 'shared/modals/Modal';
import BaseForm from 'shared/forms';
import { Field, FormElement } from '@progress/kendo-react-form';
import { FormDatePicker, FormInput, FormToggle } from 'shared/formControls';
import { CancelButton, SaveButton } from 'shared/buttons';
import { useToast } from 'shared/toast';
// Helpers
import * as Yup from 'yup';
import moment from 'moment';
// Fetch
import { upsertTruckInspections } from 'src/fetch/trucks';

const TYPE_IFTA = 3;
const TRUCK_INSPECTION_DOCUMENT_TYPE = -23;

const TruckInspectionSchema = Yup.object().shape({
  effectiveDate: Yup.date().nullable().required('Effective Date is Required'),
  expirationDate: Yup.date()
    .nullable()
    .required('Expiration Date is Required')
    .test('is-greater', 'Expiration date must be later than effective date', function (value) {
      const { effectiveDate } = this.parent;
      return moment(value).isSameOrAfter(effectiveDate);
    }),
  iftaCertificationNumber: Yup.number()
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string' && originalValue.trim() === '') {
        return null;
      }
      return value;
    })
    .when('type', {
      is: TYPE_IFTA,
      then: Yup.number().nullable().required('IFTA Certification Number is Required'),
      otherwise: Yup.number().nullable(),
    }),
});

const TruckInspectionModal = (props) => {
  const { truckId, initialValues, onClose } = props;

  const toast = useToast();

  const handleSubmit = async (values, _, onFailed, onFinish) => {
    upsertTruckInspections(truckId, values)
      .then(() => {
        onFinish?.();
        toast.success({ message: 'Truck Inspection Saved Successfully.' });
        onClose(true);
      })
      .catch((error) => {
        onFinish?.();
        console.error('Error saving Truck Inspection', error);
        toast.error(`Error saving Truck Inspection ${error}`);
      });
  };

  return (
    <Modal
      isOpen
      title={`Edit Truck Inspection - ${initialValues?.typeName}`}
      handleClose={() => onClose()}
      containerStyle="app-modal app-modal-small"
    >
      <BaseForm
        validationSchema={TruckInspectionSchema}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={(renderProps) => (
          <RenderForm {...props} {...renderProps} />
        )}
      />
    </Modal>
  );
};

const RenderForm = ({
  truckId,
  onClose,
  isSubmitting,
  valueGetter,
  onChange: setFieldValue,
  onSubmit,
}) => {
  const isIfta = valueGetter('type') === TYPE_IFTA;

  const dateFormat = 'MM-DD-YYYY';

  const handleDateChanged = (name) => ({ value: date }) => {
    setFieldValue(name, { value: date ? moment(date).format(dateFormat) : null });
  };

  return (
    <FormElement>
      <fieldset className="app-modal-body">
        <div className="card-row">
          <Field
            component={FormDatePicker}
            label="Effective Date"
            name="effectiveDate"
            required
            onChange={handleDateChanged('effectiveDate')}
          />
          <Field
            component={FormDatePicker}
            label="Expiration Date"
            name="expirationDate"
            required
            onChange={handleDateChanged('expirationDate')}
          />
        </div>
        {isIfta && (
          <div className="card-row">
            <Field
              type="number"
              label="IFTA Certification Number"
              component={FormInput}
              name="iftaCertificationNumber"
              required
            />
            <Field
              name='gwiifta'
              component={FormToggle}
              label='GWI IFTA?'
              wrapperClassName='force-vertical-toggle'
            />
          </div>
        )}
        <Attachments
          source="truck"
          name='truckInspectionAttachments'
          sourceId={truckId}
          defaultDocumentType={TRUCK_INSPECTION_DOCUMENT_TYPE}
          showAttachmentsGrid={false}
        />
      </fieldset>
      <div className="app-modal-footer">
        <SaveButton onClick={onSubmit} disabled={isSubmitting} isLoading={isSubmitting} />
        <CancelButton onClick={onClose} />
      </div>
    </FormElement>
  );
};

export default TruckInspectionModal;
